import React, { useState } from 'react';
import { SUMMARY_ITEM_CATEGORY, type Summary } from '../../types/messages';
import { formatRouteDate } from '../../utils/routeInfoFormatter';
import { formatYen } from '../../utils/priceFormatter';

interface Props {
  text: string;
  summary: Summary;
  includeTax: boolean;
  onArrangementRequest: () => void;
  isRequesting: boolean;
  isRequested: boolean;
}

const ArrangementConfirmationMessage = ({
  text,
  summary,
  includeTax,
  onArrangementRequest,
  isRequesting,
  isRequested
}: Props) => {
  const styles = getStyles();
  const [agreed, setAgreed] = useState(false);
  const { items, totalPrice, totalPriceWithTax } = summary;
  const displayTotal = includeTax ? totalPriceWithTax : totalPrice;

  const buttonDisabled = !agreed || isRequesting || isRequested;
  const buttonText = isRequested ? '手配依頼済み' : isRequesting ? '処理中...' : '手配依頼をする';

  return (
    <div style={styles.container}>
      <div style={styles.messageText}>{text}</div>

      <div style={styles.summarySection}>
        <div style={styles.sectionTitle}>【選択内容】</div>
        <div style={styles.itemList}>
          {items.map(item => {
            const isHotel = item.category === SUMMARY_ITEM_CATEGORY.HOTEL;
            const checkin = formatRouteDate(item.checkinDate ?? null);
            const checkout = formatRouteDate(item.checkoutDate ?? null);
            const stayDays = item.stayDays ?? null;
            const hasHotelStayInfo = isHotel && (checkin || checkout || stayDays);
            const periodText = checkin && checkout ? `${checkin}〜${checkout}` : checkin || checkout || '';
            const nightsText = stayDays && stayDays > 0 ? `${stayDays}泊${stayDays + 1}日` : '';

            const total = includeTax ? item.priceWithTax : item.price;
            const unit = includeTax ? item.unitPriceWithTax ?? item.unitPrice : item.unitPrice;
            const showUnitBreakdown = isHotel && stayDays && stayDays > 0 && unit !== null && unit !== undefined;
            const showTaxIncluded = includeTax && item.priceWithTax !== null && item.priceWithTax !== item.price;

            return (
              <div key={item.category} style={styles.item}>
                <div style={styles.itemLabel}>{item.label}:</div>
                <div style={styles.itemContent}>
                  <div>{item.text}</div>
                  {hasHotelStayInfo && (
                    <div style={styles.hotelStayInfo}>
                      {periodText && <span>宿泊期間: {periodText}</span>}
                      {periodText && nightsText && <span> / </span>}
                      {nightsText && <span>{nightsText}</span>}
                    </div>
                  )}
                  <div style={styles.itemPrice}>
                    料金: {formatYen(total)}
                    {showUnitBreakdown && (
                      <span>
                        （{formatYen(unit)} × {stayDays}泊）
                      </span>
                    )}
                    {showTaxIncluded && <span>（税込 {formatYen(item.priceWithTax)}）</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={styles.totalRow}>
        <span style={styles.totalLabel}>合計金額：</span>
        <span style={styles.totalPrice}>
          ¥{displayTotal.toLocaleString()}
          {includeTax ? '（税込）' : ''}
        </span>
      </div>

      <div style={styles.termsBlock}>
        <p style={styles.termsText}>
          予約（決済）完了後からご依頼いただいた各商品のキャンセル規程が適用されます。
          <br />
          ご依頼いただいた各商品について、在庫情報のタイムラグ等によりご希望に添えなかった場合は代替案をご連絡いたしますのでご了承ください。
          <br />
          <br />
          予約（決済）時に弊社の
          <a href="https://aitravel.cloud/tos/" target="_blank" rel="noopener noreferrer" style={styles.link}>
            利用規約
          </a>
          、
          <a
            href="https://d14vxaqxdw6ji3.cloudfront.net/images/travel_terms.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            旅行業約款
          </a>
          ・
          <a
            href="https://d14vxaqxdw6ji3.cloudfront.net/images/requirements.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            手配旅行条件書
          </a>
          、
          <a
            href="https://transferdata.co.jp/privacy-policy#a"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.link}
          >
            プライバシーポリシー
          </a>
          に同意するものとします。本依頼を行う者は、本サービスを正当に利用する権限を有することを保証します。
        </p>
        <label style={styles.termsCheckbox}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            style={styles.checkbox}
          />
          <span style={styles.termsCheckboxLabel}>上記に同意する</span>
        </label>
      </div>

      <div style={styles.buttonSection}>
        <button
          type="button"
          onClick={onArrangementRequest}
          disabled={buttonDisabled}
          style={{
            ...styles.primaryButton,
            ...(isRequested ? styles.buttonRequested : {}),
            ...(buttonDisabled ? styles.buttonDisabled : {})
          }}
        >
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const getStyles = () => ({
  container: {
    backgroundColor: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '16px',
    maxWidth: '480px'
  },
  messageText: {
    fontSize: '14px',
    color: '#333',
    lineHeight: '1.5'
  },
  summarySection: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  itemList: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '12px'
  },
  item: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: '4px'
  },
  itemLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#666'
  },
  itemContent: {
    fontSize: '13px',
    color: '#333',
    paddingLeft: '8px'
  },
  itemPrice: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px'
  },
  hotelStayInfo: {
    fontSize: '12px',
    color: '#666',
    marginTop: '2px'
  },
  totalRow: {
    display: 'flex' as const,
    alignItems: 'baseline' as const,
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid #e0e0e0'
  },
  totalLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333'
  },
  totalPrice: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333'
  },
  buttonSection: {
    display: 'flex' as const,
    gap: '12px',
    marginTop: '8px'
  },
  primaryButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: '#1976d2',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer' as const
  },
  buttonRequested: {
    backgroundColor: '#9e9e9e'
  },
  buttonDisabled: {
    cursor: 'not-allowed' as const,
    backgroundColor: '#c4c4c4',
    opacity: 1
  },
  termsBlock: {
    backgroundColor: '#fafafa',
    border: '1px solid #ececec',
    borderRadius: '4px',
    padding: '12px'
  },
  termsText: {
    fontSize: '11px',
    color: '#666666',
    lineHeight: '1.7',
    margin: '0 0 10px'
  },
  termsCheckbox: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    gap: '8px',
    cursor: 'pointer' as const,
    userSelect: 'none' as const
  },
  checkbox: {
    width: '16px',
    height: '16px',
    accentColor: '#0080cb',
    cursor: 'pointer' as const,
    margin: 0
  },
  termsCheckboxLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3a3a3a'
  },
  link: {
    color: '#0080cb',
    textDecoration: 'underline'
  }
});

export default ArrangementConfirmationMessage;
